import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, ChevronDown, Menu, X, Settings, Brain, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

export default function Navbar() {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    setUserMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const close = () => setUserMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [userMenuOpen]);

  const isAppRoute =
    [
      '/dashboard',
      '/settings',
      '/agent',
      '/admin',
      '/onboarding',
      '/search',
      '/graph',
      '/reports',
    ].includes(location.pathname) || location.pathname.startsWith('/exceptions/');
  const isDocsAsApp = user && location.pathname === '/docs';
  if (isAppRoute || isDocsAsApp) return null;

  const landingLinks = [
    { label: 'Home', href: '/' },
    { label: 'Product', href: '/product' },
    { label: 'Try demo', href: '/demo' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Docs', href: '/docs' },
  ];

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to={user ? '/dashboard' : '/'} className="navbar-logo">
          <div className="logo-icon">
            <img src="/logo-mark.svg" alt="" aria-hidden="true" width={22} height={22} />
          </div>
          <span className="logo-text">
            SubSec<span className="logo-accent">AI</span>
          </span>
        </Link>

        {!user && (
          <div className="navbar-links desktop">
            {landingLinks.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="nav-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className={({ isActive }) =>
                    `nav-link${isActive ? ' nav-link--active active' : ''}`
                  }
                  end={link.href === '/'}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </div>
        )}

        <div className="navbar-actions">
          {user ? (
            <>
              <Link to="/dashboard" className="btn btn-ghost">
                <LayoutDashboard size={18} />
                Dashboard
              </Link>
              <div className={`user-menu ${userMenuOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="user-menu-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserMenuOpen((v) => !v);
                  }}
                  aria-label="Account menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <div className="user-avatar">{user.name?.[0] || user.email?.[0]}</div>
                  <ChevronDown size={14} />
                </button>
                <div className="user-menu-dropdown" onClick={() => setUserMenuOpen(false)}>
                  <Link to="/agent">
                    <Brain size={16} /> Agent
                  </Link>
                  <hr />
                  <Link to="/settings">
                    <Settings size={16} /> Settings
                  </Link>
                  <hr />
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                    }}
                  >
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost">
                Sign In
              </Link>
              <Link to="/register" className="btn btn-primary">
                Start Free
              </Link>
            </>
          )}
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="mobile-menu">
          {!user &&
            landingLinks.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)}>
                  {link.label}
                </Link>
              )
            )}
          {!user && <hr />}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                Dashboard
              </Link>
              <Link to="/agent" onClick={() => setMobileOpen(false)}>
                Agent
              </Link>
              <Link to="/settings" onClick={() => setMobileOpen(false)}>
                Settings
              </Link>
              <button
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                Sign In
              </Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className="btn btn-primary">
                Start Free
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
